import { FC } from 'react';
import Head from 'next/head';
import LogoShareOg from '/public/icons/shared/logo-share-og.png';
import getConfig from 'next/config';

interface SeoProps {
  title: string;
  description: string;
  image?: string;
  canonical: string;
  pagePath: string;
  noIndex: boolean;
  noFollow: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  components?: () => void | any;
}

/**
 * It's a function that takes in a bunch of props and returns a bunch of meta tags.
 * @param  - FC<SeoProps> = ({
 * @returns A React component.
 */
const Seo: FC<SeoProps> = ({
  description,
  title,
  image,
  pagePath,
  canonical,
  noIndex,
  noFollow,
  components,
}) => {
  const { publicRuntimeConfig } = getConfig();
  const { nextPublicBaseUrl } = publicRuntimeConfig;

  const handleCanonicalUrl = (url: string): string => url.split(/[?#]/)[0];

  return (
    <Head>
      <meta name='viewport' content='width=device-width, initial-scale=1.0' />
      <title>{title}</title>
      <link
        rel='canonical'
        href={handleCanonicalUrl(
          canonical || pagePath === '/' ? '' : `${nextPublicBaseUrl}${pagePath}`
        )}
      />
      {/* It's a link tag that is used to tell search engines that there are other versions
      of this page in other languages.  */}
      <link
        rel='alternate'
        href={handleCanonicalUrl(`${nextPublicBaseUrl}en${pagePath?.slice(2)}`)}
        hrefLang='en'
      />
      <link
        rel='alternate'
        href={handleCanonicalUrl(`${nextPublicBaseUrl}en${pagePath?.slice(2)}`)}
        hrefLang='x-default'
      />
      <link
        rel='alternate'
        href={handleCanonicalUrl(`${nextPublicBaseUrl}ar${pagePath?.slice(2)}`)}
        hrefLang='ar'
      />
      <meta name='description' content={description} />
      <meta
        name='image'
        content={image ? image : `${nextPublicBaseUrl}${LogoShareOg.src.slice(1)}`}
      />

      <meta property='image:width' content='250' />
      <meta property='image:height' content='250' />
      {/*  facebook */}
      <meta property='og:title' content={title} />
      <meta property='og:description' content={description} />
      <meta
        property='og:image'
        content={image ? image : `${nextPublicBaseUrl}${LogoShareOg.src.slice(1)}`}
      />
      <meta property='og:image:width' content='250' />
      <meta property='og:image:height' content='250' />
      <meta
        property='og:url'
        content={`${nextPublicBaseUrl}${pagePath === '/' ? '' : pagePath}`}
      />
      <meta property='og:type' content='article' />
      <meta property='og:site_name' content='muvi Cinemas' />
      {/*  twitter */}
      <meta property='twitter:card' content='summary_large_image' />
      <meta property='twitter:site' content='@muvicinemas' />
      <meta property='twitter:creator' content='@muvicinemas' />
      <meta name='twitter:title' content={title} />
      <meta name='twitter:description' content={description} />
      <meta
        name='twitter:image'
        content={image ? image : `${nextPublicBaseUrl}${LogoShareOg.src.slice(1)}`}
      />
      <meta property='twitter:image:width' content='250' />
      <meta property='twitter:image:height' content='250' />
      <meta
        property='twitter:url'
        content={`${nextPublicBaseUrl}${pagePath === '/' ? '' : pagePath}`}
      />
      {(noIndex || noFollow) && (
        <meta
          name='robots'
          content={
            noIndex && noFollow ? 'noindex,nofollow' : noIndex ? 'noindex' : 'nofollow'
          }
        />
      )}
      {!!components && components()}
    </Head>
  );
};

export default Seo;
